import { Icon } from '@iconify/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Divisor, useDivisor } from '../components/Divisor';
import { Ponto, Tag } from '../components/Sga';
import { useAuth } from '../lib/auth';
import {
  estaEmbutido,
  preferenciaSalva,
  salvarPreferencia,
  seguirTemaDoPai,
  temaDaUrl,
  temaDoSistema,
  type Preferencia,
  type Tema,
} from '../lib/embutido';
import { kindDoContainer } from '../lib/format';
import type { ContainerSaude, Tenant } from '../lib/types';

export type Tela = 'dashboard' | 'conexoes' | 'setores' | 'clientes' | 'filas' | 'logs' | 'marca';

const NAV: { tela: Tela; rotulo: string; icone: string }[] = [
  { tela: 'dashboard', rotulo: 'Dashboard', icone: 'mdi:view-dashboard-outline' },
  { tela: 'conexoes', rotulo: 'Conexões', icone: 'mdi:lan-connect' },
  { tela: 'setores', rotulo: 'Setores', icone: 'mdi:account-group-outline' },
  { tela: 'clientes', rotulo: 'Clientes e links', icone: 'mdi:domain' },
  { tela: 'filas', rotulo: 'Filas e Anti-Ban', icone: 'mdi:format-list-numbered' },
  { tela: 'logs', rotulo: 'Logs de integração', icone: 'mdi:text-box-search-outline' },
  { tela: 'marca', rotulo: 'Marca e atendimento', icone: 'mdi:palette-outline' },
];

interface Props {
  tela: Tela;
  aoNavegar: (t: Tela) => void;
  containers: ContainerSaude[];
  totalConexoes: number;
  totalFila: number;
  busca: string;
  aoBuscar: (v: string) => void;
  incidentes: number;
  wsConectado: boolean;
  aoVerNoCelular: () => void;
  children: ReactNode;
}

export function Shell({
  tela,
  aoNavegar,
  containers,
  totalConexoes,
  totalFila,
  busca,
  aoBuscar,
  incidentes,
  wsConectado,
  aoVerNoCelular,
  children,
}: Props) {
  const { usuario, tenants, tenantAtivo, trocarTenant, sair } = useAuth();
  const [tenantAberto, setTenantAberto] = useState(false);
  // Dentro do SGA Desk o painel e uma aba dele, nao um aplicativo: usuario,
  // busca, sino, tema e sair ja estao desenhados logo acima.
  const embutido = useMemo(estaEmbutido, []);
  // `auto` segue o Desk; um valor fixo é escolha explícita da pessoa.
  //
  // O parâmetro da URL vence sempre: quando o Desk diz o tema dele, ele está
  // certo por definição — o painel é uma aba dentro dele, e discordar da
  // moldura é o que produz a janela branca no meio do escuro.
  const [preferencia, setPreferencia] = useState<Preferencia>(
    () => (temaDaUrl() ? 'auto' : preferenciaSalva()),
  );
  const [temaDoDesk, setTemaDoDesk] = useState<Tema>(() => temaDaUrl() ?? temaDoSistema());
  const tema: Tema = preferencia === 'auto' ? temaDoDesk : preferencia;
  const popover = useRef<HTMLDivElement | null>(null);

  // A sidebar guarda a largura em pixels: o conteudo fica com o resto. Medir
  // a partir da borda esquerda da area, e nao do movimento do ponteiro, faz o
  // arrasto acompanhar o cursor mesmo se ele sair da alca.
  const divisorSidebar = useDivisor({
    chave: 'sidebar',
    inicial: 224,
    min: 170,
    max: 380,
    calcular: (clientX, area) => clientX - area.left,
    rotulo: 'Largura do menu lateral',
  });

  useEffect(() => {
    document.documentElement.dataset.theme = tema;
  }, [tema]);

  const trocarTema = (t: Tema) => {
    setPreferencia(t);
    salvarPreferencia(t);
  };

  /** Volta a seguir o SGA Desk. */
  const seguirODesk = () => {
    setPreferencia('auto');
    salvarPreferencia('auto');
  };

  // Embutido, quem manda no tema e o Desk. Sem isto o painel abriria claro
  // dentro de um Desk escuro, e ficaria assim ate alguem recarregar.
  //
  // O sinal do Desk **sempre** e guardado, mesmo com preferencia fixa: se a
  // pessoa voltar para `auto`, o tema certo ja esta ali, sem esperar a
  // proxima troca no Desk.
  useEffect(() => {
    if (!embutido) return;
    return seguirTemaDoPai(setTemaDoDesk);
  }, [embutido]);

  // Fecha o popover ao clicar fora — mesmo comportamento do protótipo.
  useEffect(() => {
    if (!tenantAberto) return;
    const aoClicar = (e: MouseEvent) => {
      if (!popover.current?.contains(e.target as Node)) setTenantAberto(false);
    };
    document.addEventListener('mousedown', aoClicar);
    return () => document.removeEventListener('mousedown', aoClicar);
  }, [tenantAberto]);

  const escolher = async (t: Tenant) => {
    setTenantAberto(false);
    if (t.id !== tenantAtivo?.id) await trocarTenant(t);
  };

  const iniciais = (usuario?.nome ?? 'SGA')
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <div className={embutido ? 'app app--embutido' : 'app'}>
      {!embutido && (
      <header className="app__header" style={{ position: 'relative' }}>
        {/* A logo abre o diagrama de arquitetura. É `<a>` de propósito: aceita
            ctrl+clique e clique do meio para abrir em aba nova, o que um botão
            com `window.open` não daria. */}
        <a
          className="app__marca"
          href="/arquitetura.html"
          target="_blank"
          rel="noopener"
          title="Ver o diagrama de arquitetura e o fluxo de mensagem"
        >
          <img src="/logos/sga-logo-branca.svg" alt="SGA" />
          <div>
            <div className="app__marca-nome">Messenger</div>
            <div className="app__marca-sub">Plataforma omnichannel</div>
          </div>
        </a>

        <button
          type="button"
          className="empresas-chip"
          style={{ height: 30 }}
          onClick={() => setTenantAberto((v) => !v)}
          disabled={!usuario?.pode_trocar_tenant && tenants.length <= 1}
        >
          <Icon icon={tenantAtivo?.icone ?? 'mdi:office-building-outline'} width={15} />
          {tenantAtivo?.nome ?? 'Selecione o tenant'}
          <span className="sep">•</span>
          {tenantAtivo?.conexoes ?? 0} conexões
          <Icon icon="mdi:chevron-down" width={15} />
        </button>

        {tenantAberto && (
          <div className="sga-popover" style={{ left: 300 }} ref={popover}>
            <div className="sga-popover__head">
              <span>Trocar de tenant</span>
              {usuario?.pode_trocar_tenant && <Tag>ADMIN GLOBAL</Tag>}
            </div>
            {tenants.map((t) => (
              <button
                key={t.id}
                type="button"
                className="sga-popover__item"
                aria-current={t.id === tenantAtivo?.id}
                onClick={() => escolher(t)}
              >
                <Icon icon={t.icone} width={18} />
                <span style={{ flex: 1 }}>
                  {t.nome}
                  <div style={{ fontSize: 11, color: 'var(--sga-fg-3)' }}>
                    {t.conexoes} conexões
                  </div>
                </span>
                {t.id === tenantAtivo?.id && <Icon icon="mdi:check-bold" width={15} />}
              </button>
            ))}
            <div className="sga-popover__rodape">Contexto aplicado a todas as telas</div>
          </div>
        )}

        <span style={{ flex: 1 }} />

        <div className="app__busca">
          <Icon icon="mdi:magnify" width={16} />
          <input
            value={busca}
            onChange={(e) => aoBuscar(e.target.value)}
            placeholder="Buscar conexão, número ou tenant..."
            aria-label="Buscar"
          />
        </div>

        <button
          type="button"
          className="app__icone-header"
          onClick={() => trocarTema(tema === 'light' ? 'dark' : 'light')}
          title={tema === 'light' ? 'Tema escuro' : 'Tema claro'}
          aria-label="Alternar tema"
        >
          <Icon icon={tema === 'light' ? 'mdi:weather-night' : 'mdi:white-balance-sunny'} width={17} />
        </button>

        <button
          type="button"
          className="app__icone-header"
          onClick={() => aoNavegar('logs')}
          title={`${incidentes} incidentes recentes`}
          aria-label="Notificações"
        >
          <Icon icon="mdi:bell-outline" width={17} />
          {incidentes > 0 && <span className="app__badge">{incidentes}</span>}
        </button>

        <button type="button" className="app__usuario" onClick={aoVerNoCelular}>
          <div>
            <div className="app__usuario-nome">{usuario?.nome ?? '—'}</div>
            <div className="app__usuario-sub">
              {usuario?.cargo ?? 'Operação'} · ver no celular
            </div>
          </div>
          <span className="avatar">{iniciais}</span>
        </button>

        {/* Sessão pode ficar salva entre visitas, então sair precisa ser
            explícito — é o que revoga os refresh tokens no gateway. */}
        <button
          type="button"
          className="app__icone-header"
          onClick={() => {
            if (window.confirm('Sair do painel? A sessão salva neste computador é descartada.')) {
              void sair();
            }
          }}
          title="Sair"
          aria-label="Sair"
        >
          <Icon icon="mdi:logout-variant" width={17} />
        </button>
      </header>
      )}

      <div className="app__corpo" ref={divisorSidebar.areaRef}>
        <nav className="app__sidebar" style={{ width: divisorSidebar.valor }}>
          {/* Embutido, o tenant vira contexto e nao seletor: o SSO ja o fixou
              no token, e uma lista que nao troca nada faz o gestor clicar,
              nao ver efeito e concluir que o painel esta quebrado. */}
          {embutido && tenantAtivo && (
            <div className="app__contexto">
              <Icon icon={tenantAtivo.icone ?? 'mdi:office-building-outline'} width={14} />
              <span className="app__contexto-nome">{tenantAtivo.nome}</span>
            </div>
          )}
          <div className="app__sidebar-rotulo">Operação</div>
          {NAV.map((item) => (
            <button
              key={item.tela}
              type="button"
              className="app__nav-item"
              aria-current={tela === item.tela ? 'page' : undefined}
              onClick={() => aoNavegar(item.tela)}
            >
              <Icon icon={item.icone} width={18} />
              {item.rotulo}
              {item.tela === 'conexoes' && totalConexoes > 0 && (
                <span className="app__nav-badge">
                  <Tag>{totalConexoes}</Tag>
                </span>
              )}
              {item.tela === 'filas' && totalFila > 0 && (
                <span className="app__nav-badge">
                  <Tag kind="warn">{totalFila}</Tag>
                </span>
              )}
            </button>
          ))}

          <div className="app__sidebar-rotulo" style={{ marginTop: 10 }}>
            Infraestrutura
          </div>
          {containers.length === 0 && (
            <div className="app__container-vazio">
              <Ponto kind="mut" />
              sem heartbeat ainda
            </div>
          )}

          {/* Grade em vez de lista: sete containers empilhados viravam uma
              coluna de texto onde nada se destacava, e nomes longos como
              "WhatsApp Cloud API" quebravam no meio da linha da versão. Em
              cartões, cada serviço é um bloco com limite próprio. */}
          <div className="app__containers">
            {containers.map((c) => (
              <div
                className="app__container-card"
                key={c.servico}
                title={`${c.nome} — ${c.status}${c.versao ? ` · ${c.versao}` : ''}`}
              >
                <span className="app__container-card__status">
                  <Ponto kind={kindDoContainer(c.status)} />
                </span>
                <span className="app__container-card__nome">{c.nome}</span>
                <span className="app__container-card__versao">
                  {c.uptime_pct !== null ? `${c.uptime_pct.toFixed(1)}%` : (c.versao ?? '—')}
                </span>
              </div>
            ))}
          </div>

          {/* Embutido, o cabeçalho não é desenhado e o botão de tema morava
              lá. Enquanto o SGA Desk não manda o tema, sem isto o gestor
              ficava preso no claro dentro de um Desk escuro, sem controle. */}
          {embutido && (
            <div className="app__tema-linha">
              <button
                type="button"
                className="app__tema-embutido"
                onClick={() => trocarTema(tema === 'light' ? 'dark' : 'light')}
                title={tema === 'light' ? 'Tema escuro' : 'Tema claro'}
              >
                <Icon
                  icon={tema === 'light' ? 'mdi:weather-night' : 'mdi:white-balance-sunny'}
                  width={15}
                />
                {tema === 'light' ? 'Tema escuro' : 'Tema claro'}
              </button>
              {preferencia !== 'auto' && (
                <button
                  type="button"
                  className="app__tema-auto"
                  onClick={seguirODesk}
                  title="Voltar a seguir o tema do SGA Desk"
                >
                  <Icon icon="mdi:autorenew" width={13} />
                  seguir o Desk
                </button>
              )}
            </div>
          )}

          <div className="app__sidebar-rodape">
            Versão Istambul
            <br />
            Gateway Rust 0.9.4 · REST + {wsConectado ? 'WS' : 'WS off'}
          </div>
        </nav>

        <Divisor {...divisorSidebar.props} />

        <main className="app__conteudo">{children}</main>
      </div>

      <footer className="app__footer" />
    </div>
  );
}
